import { Text, View } from "react-native";

import { formatCardIdentity, type GradeSeal } from "@/lib/catalog/cardIdentity";
import { cn } from "@/lib/ui/cn";
import { CardTile } from "./CardTile";
import { catalogRarityToBrand, isChaseRarity } from "./rarity";

/**
 * The hero art's width when it sits beside a price stack (TRO-775).
 *
 * Roughly half a phone's width, and narrower than `CardTile`'s standalone 240px hero, which is why
 * `CardHero` takes a `width`. The art box keeps the tile's 3:4 aspect, so at 170 wide it is 227
 * tall rather than a 5:7 237; a per-screen ratio would let two surfaces disagree about what a card
 * looks like. Both detail surfaces share this value so they cannot drift apart.
 */
export const HERO_ART_WIDTH = 170;

/**
 * The card-detail hero (#760): one focused card, rendered as a `CardTile` at its hero size.
 *
 * It is the tile's flagship surface in the brand-layer spec and one of ADR-0018's adoption targets.
 * Both detail surfaces render through it: the authenticated Dossier (`(app)/card-detail`, including
 * its degraded "dossier didn't load" path) and the public browse item page
 * (`browse/[eventId]/[itemId]`). It sits above `CardTile` because it owns two decisions those pages
 * must not make differently:
 *
 * 1. Rarity. It takes the card's raw upstream Catalog rarity string and maps it to a brand tier
 *    itself. Absent means unknown (the tile's neutral border), never "common", which is a
 *    tier the data asserts rather than a fallback.
 * 2. Foil. The shimmer is the brand layer's only named motion exception, and this is its one call
 *    site in the app. It turns on only for a chase printing (`isChaseRarity`); a page cannot opt in.
 *
 * The tile draws both the art and the name/`set · №` caption, so a page must not print its own
 * caption as well.
 *
 * `seal` arrives resolved (`formatGradeSeal`) because the two pages source it differently: the
 * Dossier from its route params or the owned unit, the storefront from the item row. At hero size
 * the tile draws it as the grader's holder (TRO-627).
 */
export function CardHero(
  { name, set, number, variant, image, rarity, seal, width, caption = "inline", testIDs }: {
    name: string;
    set?: string | null;
    number?: string | null;
    variant?: string | null;
    image?: string | null;
    /** The card's raw upstream Catalog rarity string ("Rare Holo", …), not a brand tier. */
    rarity?: string | null;
    /** The slab this unit is in, already resolved by `formatGradeSeal`. */
    seal?: GradeSeal | null;
    /** Overrides the hero's fixed 240px brand width (TRO-775), for the card-detail layout that puts
     *  the art beside a price stack at roughly half the screen. `CardTile` already lets a
     *  responsive width win over the size's own, so this is a passthrough, not a new size. */
    width?: number;
    /** Where the card's identity prints. `"inline"` (the default) is the tile's own block beneath
     *  the art. `"below"` draws the art alone, and the caller renders `CardHeroCaption`
     *  left-aligned under the whole art and prices row (TRO-775), so the identity still prints
     *  exactly once. */
    caption?: "inline" | "below";
    /** Forwarded to the tile, so a page keeps its e2e id contract on the name and caption lines. */
    testIDs?: { name?: string; identity?: string; };
  },
) {
  const tier = catalogRarityToBrand(rarity);
  return (
    <View className={cn(caption === "below" ? "items-start" : "items-center")}>
      <CardTile
        size="hero"
        width={width}
        identityPlacement={caption === "below" ? "hidden" : "inline"}
        name={name}
        set={set ?? undefined}
        number={number ?? undefined}
        variant={variant ?? undefined}
        rarity={tier}
        seal={seal ?? undefined}
        image={image ?? undefined}
        foil={isChaseRarity(tier)}
        testIDs={testIDs}
      />
    </View>
  );
}

/**
 * The hero's identity, printed below an art and prices row rather than under the art (TRO-775).
 *
 * Use it only with `CardHero caption="below"`: one draws the identity and the other does not, so a
 * screen that renders both prints the card's name once. Beside a default `CardHero` it prints the
 * name twice (#760).
 *
 * The caption comes from `formatCardIdentity`, the same call the tile makes, so "Fossil · #5/62 ·
 * Reverse Holo" reads identically here and in every grid.
 */
export function CardHeroCaption(
  { name, set, number, variant, testIDs, headingLevel = 1 }: {
    name: string;
    set?: string | null;
    number?: string | null;
    variant?: string | null;
    /** testIDs for the name and identity lines, matching `CardHero`'s. */
    testIDs?: { name?: string; identity?: string; };
    /** The name's heading level on web; 2 when the page's h1 is drawn elsewhere. */
    headingLevel?: 1 | 2;
  },
) {
  const identity = formatCardIdentity(set ?? undefined, number ?? undefined, variant ?? undefined);
  return (
    <View>
      <Text
        testID={testIDs?.name}
        // The name stays the page heading when the identity prints below the art (#531/#760).
        accessibilityRole="header"
        aria-level={headingLevel}
        numberOfLines={2}
        className="font-title text-[19px] tracking-[-0.02em] text-foreground"
      >
        {name}
      </Text>
      {identity
        ? (
          <Text
            testID={testIDs?.identity}
            // Truncates rather than wraps: this sits under a fixed-height row and a second line
            // would push the price stack's baseline off the art it is meant to align with.
            numberOfLines={1}
            className="mt-0.5 font-body text-[13px] text-muted-foreground"
          >
            {identity}
          </Text>
        )
        : null}
    </View>
  );
}
